import React, { useState } from "react";
import { motion } from "framer-motion";
import { useColorMode } from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Title, Tooltip, Legend, Filler,
} from "chart.js";
import PartnerIllustration from "../../assets/images/PartnerIllusration.gif";
// import PartnerWhySection from "./PartnerWhySection";
import Test01 from "../../assets/images/private/Test01.png";
import Test02 from "../../assets/images/private/Test02.png";
import Test03 from "../../assets/images/private/Test03.png";
import Test04 from "../../assets/images/private/Test04.png";
import Test05 from "../../assets/images/private/Test05.png";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,800&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');`;

/* ─── DATA ─────────────────────────────────────────────────────────────────── */
const PARTNER_TYPES = [
  { key: "student",    label: "College Student / Campus Ambassador", percent: 15, icon: "🎓" },
  { key: "faculty",    label: "Faculty / Mentor",                    percent: 20, icon: "👨‍🏫" },
  { key: "individual", label: "Individual / Influencer",             percent: 25, icon: "🌟" },
  { key: "college",    label: "Principal / Institution",             percent: 30, icon: "🏛️" },
  { key: "training",   label: "Training Partner / EdTech",           percent: 35, icon: "🏢" },
];

const WHY_CARDS = [
  { icon: "💰", title: "Transparent Commissions",   desc: "Clearly defined commission tiers from 15–35%. No fine print, no surprises — you always know exactly what you earn.",       accent: "#028EC4" },
  { icon: "📈", title: "Performance Bonuses",        desc: "Volume, consistency, and institutional bonuses stack on top of your base commission. The more you grow, the more you earn.", accent: "#F59E0B" },
  { icon: "🎯", title: "Real Career Outcomes",       desc: "You're not selling a course — you're enabling real careers. Our bootcamps are built for industry readiness, not certificates.", accent: "#10B981" },
  { icon: "🤝", title: "Dedicated Partner Support",  desc: "A dedicated partner success manager, ready-made marketing assets, and a private partner dashboard to track everything.",     accent: "#6366F1" },
  { icon: "⚡", title: "Monthly Payouts",             desc: "Commissions calculated on confirmed enrollments, released on a monthly cycle with full breakdowns and statements.",          accent: "#EC4899" },
  { icon: "🌐", title: "Grow With the Network",      desc: "Join a growing ecosystem of 50+ partner institutions, mentors, and influencers across India — and expand with us.",           accent: "#14B8A6" },
];

const TESTIMONIALS = [
  { name: "Suresh Kumar",  role: "Faculty – Chennai",    image: Test01, text: "GrapplTech's programs align perfectly with industry needs and student outcomes." },
  { name: "Ramana Reddy",  role: "Faculty – Tirupati",   image: Test04, text: "The bootcamp helped me transition confidently into real-world development." },
  { name: "Rohit Sharma",  role: "Faculty – Hyderabad",  image: Test02, text: "Live mentorship and projects made learning practical and effective." },
  { name: "Lakshmi Devi",  role: "Faculty – Anantapur",  image: Test03, text: "The partner model is transparent and ideal for academic collaboration." },
  { name: "Karthik S",     role: "Student – Tamil Nadu", image: Test05, text: "Clear curriculum and guidance helped me choose the right tech career path." },
];

const PAYOUT_STEPS = [
  { icon: "📋", step: "01", title: "Referral Registered",    desc: "Your unique referral link tracks every lead you bring in, in real time." },
  { icon: "✅", step: "02", title: "Enrollment Confirmed",   desc: "Commission is locked in the moment the learner completes payment." },
  { icon: "📊", step: "03", title: "Monthly Statement",      desc: "A detailed earnings statement lands in your inbox every month." },
  { icon: "💸", step: "04", title: "Payout Released",        desc: "Funds are transferred directly to your account within the first week of every month." },
];

/* ─── ICONS ────────────────────────────────────────────────────────────────── */
const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const StarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="1">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const QuoteIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" opacity="0.12">
    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
  </svg>
);

/* ─── COMMISSION CALCULATOR ────────────────────────────────────────────────── */
function CommissionCalculator({ isDark }) {
  const [partner, setPartner]     = useState(PARTNER_TYPES[0]);
  const [referrals, setReferrals] = useState(30);
  const [conversion, setConversion] = useState(70);
  const [price, setPrice]         = useState(7000);
  const [yearlyView, setYearlyView] = useState(false);

  const cardBg    = isDark ? "#16161f"                : "#ffffff";
  const cardBorder= isDark ? "rgba(255,255,255,0.09)" : "#E5E7EB";
  const textPri   = isDark ? "rgba(255,255,255,0.93)" : "#111827";
  const textMuted = isDark ? "rgba(255,255,255,0.50)" : "#6B7280";
  const inputBg   = isDark ? "rgba(255,255,255,0.06)" : "#F9FAFB";
  const inputBord = isDark ? "rgba(255,255,255,0.12)" : "#D1D5DB";
  const bonusBg   = isDark ? "rgba(2,142,196,0.08)"   : "rgba(2,142,196,0.05)";
  const bonusBord = isDark ? "rgba(2,142,196,0.2)"    : "rgba(2,142,196,0.15)";
  const divider   = isDark ? "rgba(255,255,255,0.08)" : "#E5E7EB";

  const enrollments    = Math.round((referrals * conversion) / 100);
  const grossRevenue   = enrollments * price;
  const baseCommission = (grossRevenue * partner.percent) / 100;

  let volumeBonus = 0;
  if (enrollments >= 100)      volumeBonus = 0.05;
  else if (enrollments >= 50)  volumeBonus = 0.03;
  else if (enrollments >= 25)  volumeBonus = 0.02;

  const consistencyBonus   = enrollments >= 20 ? 0.02 : 0;
  const institutionalBonus = (partner.key === "college" || partner.key === "training") ? 0.05 : 0;
  const totalBonusPct      = volumeBonus + consistencyBonus + institutionalBonus;
  const totalBonus         = baseCommission * totalBonusPct;
  const monthlyEarnings    = baseCommission + totalBonus;
  const yearlyEarnings     = monthlyEarnings * 12;

  const fmt = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

  const chartLabels = yearlyView ? ["Q1","Q2","Q3","Q4"] : ["Week 1","Week 2","Week 3","Week 4"];
  const base = yearlyView ? yearlyEarnings : monthlyEarnings;
  const chartData = {
    labels: chartLabels,
    datasets: [{
      label: yearlyView ? "Quarterly Earnings (₹)" : "Monthly Growth (₹)",
      data: [base * 0.2, base * 0.45, base * 0.72, base],
      borderColor: "#028EC4",
      backgroundColor: isDark ? "rgba(2,142,196,0.12)" : "rgba(2,142,196,0.08)",
      tension: 0.4, fill: true, pointRadius: 5,
      pointBackgroundColor: "#028EC4", pointBorderColor: cardBg, pointBorderWidth: 2,
    }],
  };
  const chartOptions = {
    responsive: true, maintainAspectRatio: true,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { color: divider }, ticks: { color: textMuted, font: { family: "'DM Sans', sans-serif", size: 12 } } },
      y: { grid: { color: divider }, ticks: { color: textMuted, font: { family: "'DM Sans', sans-serif", size: 11 }, callback: v => "₹" + Number(v).toLocaleString("en-IN") } },
    },
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px", borderRadius: 10, fontSize: 14,
    background: inputBg, border: `1px solid ${inputBord}`,
    color: textPri, outline: "none", fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6, display: "block" };

  return (
    <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 22, padding: "clamp(24px,4vw,48px)", maxWidth: 960, margin: "0 auto", boxShadow: isDark ? "0 8px 40px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.08)" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 8px" }}>Interactive Tool</p>
        <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 800, color: textPri, margin: "0 0 8px" }}>
          Commission & Earnings Calculator
        </h2>
        <p style={{ fontSize: 14, color: textMuted, margin: 0 }}>Transparent breakdown of commissions, bonuses, and long-term earnings</p>
      </div>

      {/* Partner type selector */}
      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>Partner Type</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 10 }}>
          {PARTNER_TYPES.map(p => (
            <button key={p.key} onClick={() => setPartner(p)} style={{
              padding: "10px 14px", borderRadius: 12, cursor: "pointer", transition: "all 0.18s",
              border: `1.5px solid ${partner.key === p.key ? "#028EC4" : inputBord}`,
              background: partner.key === p.key ? "rgba(2,142,196,0.12)" : inputBg,
              color: partner.key === p.key ? "#028EC4" : textMuted,
              fontSize: 12.5, fontWeight: partner.key === p.key ? 700 : 500,
              textAlign: "left", display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ fontSize: 16 }}>{p.icon}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 11 }}>{p.percent}% base</div>
                <div style={{ fontSize: 10.5, opacity: 0.8, marginTop: 1 }}>{p.label.split("/")[0].trim()}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Inputs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Referrals / Month", value: referrals,  set: setReferrals },
          { label: "Conversion Rate (%)", value: conversion, set: setConversion },
          { label: "Avg Bootcamp Fee (₹)", value: price, set: setPrice },
        ].map(({ label, value, set }) => (
          <div key={label}>
            <label style={labelStyle}>{label}</label>
            <input type="number" value={value} onChange={e => set(Number(e.target.value))} style={inputStyle} />
          </div>
        ))}
      </div>

      <div style={{ height: 1, background: divider, margin: "0 0 24px" }} />

      {/* Results grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 14, marginBottom: 24 }}>
        {[
          { label: "Enrollments",           value: enrollments, highlight: false },
          { label: "Gross Revenue",         value: fmt(grossRevenue), highlight: false },
          { label: "Base Commission",       value: fmt(baseCommission), highlight: true },
          { label: "Bonus Earnings",        value: fmt(totalBonus), highlight: false },
          { label: "Monthly Earnings",      value: fmt(monthlyEarnings), highlight: true, big: true },
          { label: "Yearly Earnings",       value: fmt(yearlyEarnings), highlight: false },
        ].map(({ label, value, highlight, big }) => (
          <div key={label} style={{
            background: highlight ? "rgba(2,142,196,0.08)" : inputBg,
            border: `1px solid ${highlight ? "rgba(2,142,196,0.25)" : inputBord}`,
            borderRadius: 12, padding: "14px 16px",
          }}>
            <p style={{ fontSize: 11, color: textMuted, margin: "0 0 4px", fontWeight: 600, letterSpacing: "0.06em" }}>{label}</p>
            <p style={{ fontSize: big ? 20 : 16, fontWeight: 800, color: highlight ? "#028EC4" : textPri, margin: 0, fontFamily: "'Bricolage Grotesque', sans-serif" }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Bonus tiers */}
      <div style={{ background: bonusBg, border: `1px solid ${bonusBord}`, borderRadius: 14, padding: "18px 20px", marginBottom: 24 }}>
        <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 12px" }}>🎁 Active Bonus Tiers</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
          {[
            ["25+ enrollments", "2% volume bonus",      enrollments >= 25],
            ["50+ enrollments", "3% volume bonus",      enrollments >= 50],
            ["100+ enrollments","5% volume bonus",      enrollments >= 100],
            ["Institution / Training","+ 5% bonus",     institutionalBonus > 0],
            ["20+ monthly enrollments","Consistency +2%", consistencyBonus > 0],
          ].map(([cond, reward, active]) => (
            <div key={cond} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 20, height: 20, borderRadius: "50%", background: active ? "rgba(16,185,129,0.2)" : inputBg, border: `1px solid ${active ? "rgba(16,185,129,0.4)" : inputBord}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 10, color: active ? "#10B981" : textMuted }}>
                {active ? "✓" : "·"}
              </span>
              <span style={{ fontSize: 12.5, color: active ? textPri : textMuted, fontWeight: active ? 600 : 400 }}>
                {cond} → <strong style={{ color: active ? "#10B981" : textMuted }}>{reward}</strong>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Toggle + Chart */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginBottom: 20 }}>
        {["Monthly","Yearly"].map(v => (
          <button key={v} onClick={() => setYearlyView(v === "Yearly")} style={{
            padding: "7px 20px", borderRadius: 999, fontSize: 13, fontWeight: 600,
            cursor: "pointer", transition: "all 0.18s", border: `1px solid ${inputBord}`,
            background: (yearlyView ? "Yearly" : "Monthly") === v ? "#028EC4" : inputBg,
            color: (yearlyView ? "Yearly" : "Monthly") === v ? "#fff" : textMuted,
          }}>{v}</button>
        ))}
      </div>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
}

/* ─── MAIN PAGE ────────────────────────────────────────────────────────────── */
export default function CampusAmbassador() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  const pageBg      = isDark ? "#09090f"                : "#F8F9FB";
  const heroBorder  = isDark ? "rgba(255,255,255,0.07)" : "#E5E7EB";
  const heroGlow    = isDark ? "rgba(2,142,196,0.11)"   : "rgba(2,142,196,0.06)";
  const textPri     = isDark ? "rgba(255,255,255,0.93)" : "#111827";
  const textMuted   = isDark ? "rgba(255,255,255,0.50)" : "#6B7280";
  const pillBg      = isDark ? "rgba(2,142,196,0.12)"   : "rgba(2,142,196,0.08)";
  const pillBorder  = isDark ? "rgba(2,142,196,0.35)"   : "rgba(2,142,196,0.22)";
  const statBg      = isDark ? "rgba(255,255,255,0.04)" : "#ffffff";
  const statBorder  = isDark ? "rgba(255,255,255,0.08)" : "#E5E7EB";
  const cardBg      = isDark ? "#16161f"                : "#ffffff";
  const cardBorder  = isDark ? "rgba(255,255,255,0.09)" : "#E5E7EB";
  const cardShadow  = isDark ? "0 4px 18px rgba(0,0,0,0.3)" : "0 2px 10px rgba(0,0,0,0.06)";
  // const sectionBg   = isDark ? "rgba(255,255,255,0.03)" : "rgba(2,142,196,0.03)";
  // const sectionBord = isDark ? "rgba(255,255,255,0.07)" : "rgba(2,142,196,0.12)";
  const tCardBg     = isDark ? "#13131e"                : "#ffffff";
  const tCardBorder = isDark ? "rgba(255,255,255,0.08)" : "#E5E7EB";
  const payoutBg    = isDark ? "rgba(16,185,129,0.07)"  : "rgba(16,185,129,0.04)";
  const payoutBord  = isDark ? "rgba(16,185,129,0.22)"  : "rgba(16,185,129,0.18)";

  return (
    <>
      <style>{`
        ${FONT_LINK}
        * { box-sizing: border-box; }
        .ca-page { font-family: 'DM Sans', sans-serif; }
        .ca-marquee-wrap { overflow: hidden; position: relative; }
        .ca-marquee-wrap::before, .ca-marquee-wrap::after {
          content: ""; position: absolute; top: 0; bottom: 0; width: 80px; z-index: 2; pointer-events: none;
        }
        .ca-marquee-wrap::before { left: 0; background: linear-gradient(to right, ${pageBg}, transparent); }
        .ca-marquee-wrap::after  { right: 0; background: linear-gradient(to left, ${pageBg}, transparent); }
        @keyframes ca-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .ca-marquee-track { display: flex; gap: 20px; width: max-content; animation: ca-scroll 40s linear infinite; }
        .ca-marquee-track:hover { animation-play-state: paused; }
        .ca-split { display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: center; }
        @media (max-width: 700px) {
          .ca-split { grid-template-columns: 1fr; gap: 28px; }
          .ca-split .ca-img { order: 2; }
          .ca-split .ca-text { order: 1; }
        }
      `}</style>

      <div className="ca-page" style={{ background: pageBg, minHeight: "100vh", padding: "0 0 100px", color: textPri }}>

        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <div style={{ textAlign: "center", padding: "80px 24px 64px", background: `radial-gradient(ellipse 80% 45% at 50% 0%, ${heroGlow} 0%, transparent 70%)`, borderBottom: `1px solid ${heroBorder}` }}>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: pillBg, border: `1px solid ${pillBorder}`, color: "#028EC4", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", padding: "6px 16px", borderRadius: 999, marginBottom: 22 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#028EC4" }} />
              Performance-Driven Collaboration
            </span>
            <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(2.5rem, 6.5vw, 4.8rem)", fontWeight: 800, lineHeight: 1.06, margin: "0 0 18px", color: textPri }}>
              GrapplTech's{" "}
              <span style={{ background: "linear-gradient(135deg, #028EC4, #38BDF8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Partner Program</span>
            </h1>
            <p style={{ fontSize: "clamp(14px, 2vw, 17px)", color: textMuted, maxWidth: 580, margin: "0 auto 38px", lineHeight: 1.8 }}>
              A long-term, performance-driven collaboration for students, faculty, institutions, and professionals to earn consistently while enabling real tech careers.
            </p>
            {/* Stats */}
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px 10px", marginBottom: 36 }}>
              {[["🏛️","50+","Partner Institutions"],["💰","15–35%","Commission Tiers"],["⚡","Monthly","Payouts"],["🎓","6,000+","Learners Enabled"]].map(([icon,v,l]) => (
                <div key={l} style={{ textAlign: "center", padding: "11px 20px", background: statBg, border: `1px solid ${statBorder}`, borderRadius: 12, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 18 }}>{icon}</span>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1rem,2vw,1.3rem)", fontWeight: 800, color: textPri, lineHeight: 1 }}>{v}</div>
                    <div style={{ fontSize: 11.5, color: textMuted, marginTop: 2 }}>{l}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
              <a href="https://tally.so/r/obeQab" target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#028EC4", color: "#fff", fontWeight: 700, fontSize: 14, padding: "12px 24px", borderRadius: 12, textDecoration: "none", boxShadow: "0 4px 16px rgba(2,142,196,0.35)", transition: "filter 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.1)"}
                onMouseLeave={e => e.currentTarget.style.filter = "none"}
              >
                Become a Partner <ArrowIcon />
              </a>
              <a href="mailto:partners@grappl-tech.in"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: "#028EC4", fontWeight: 700, fontSize: 14, padding: "12px 24px", borderRadius: 12, textDecoration: "none", border: "1px solid #028EC455", transition: "background 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(2,142,196,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                partners@grappl-tech.in
              </a>
            </div>
          </motion.div>
        </div>

        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>

          {/* ── ILLUSTRATION + WHY TEXT ───────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 72 }}>
            <div className="ca-split">
              <div className="ca-img" style={{ display: "flex", justifyContent: "center" }}>
                <img src={PartnerIllustration} alt="Partner with GrapplTech" style={{ width: "100%", maxWidth: 420, borderRadius: 20, border: `1px solid ${cardBorder}` }} />
              </div>
              <div className="ca-text">
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 10px" }}>Why Partner With Us</p>
                <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.6rem,3vw,2.3rem)", fontWeight: 800, color: textPri, margin: "0 0 18px", lineHeight: 1.15 }}>
                  Built Around Real Career Outcomes
                </h2>
                {[
                  "GrapplTech is built around one core principle — <strong>real career outcomes</strong>. Every bootcamp, workshop, and learning pathway is designed to move learners closer to industry readiness, not just certifications.",
                  "When you partner with GrapplTech, you're not just referring a course — you're enabling students and professionals to gain practical skills, mentorship, and confidence that translate directly into real-world opportunities.",
                  "Our partner ecosystem is transparent, fair, and sustainable. Commissions are clearly defined, payouts are timely, and performance is tracked openly so partners always know where they stand.",
                ].map((p, i) => (
                  <p key={i} style={{ fontSize: 14.5, color: textMuted, lineHeight: 1.8, margin: "0 0 14px" }} dangerouslySetInnerHTML={{ __html: p.replace("<strong>", `<strong style="color:${textPri}">`).replace("</strong>","</strong>") }} />
                ))}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
                  {["Students","Faculty","Institutions","Influencers","EdTech Partners"].map(tag => (
                    <span key={tag} style={{ fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 999, background: isDark ? "rgba(2,142,196,0.12)" : "rgba(2,142,196,0.08)", border: "1px solid rgba(2,142,196,0.25)", color: "#028EC4" }}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── WHY CARDS ────────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 80 }}>
            <div style={{ marginBottom: 32 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#F59E0B", margin: "0 0 6px" }}>What You Get</p>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 800, color: textPri, margin: "0 0 8px" }}>Why Partner with GrapplTech?</h2>
              <p style={{ color: textMuted, fontSize: 14, margin: 0, maxWidth: 480 }}>Every partner gets structure, support, and a transparent earnings system from day one.</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 18 }}>
              {WHY_CARDS.map((c, i) => (
                <motion.div key={c.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                  whileHover={{ y: -5, boxShadow: `0 16px 40px rgba(0,0,0,0.14), 0 0 0 1.5px ${c.accent}44` }}
                  style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 18, padding: "24px 22px", position: "relative", overflow: "hidden", boxShadow: cardShadow }}
                >
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: c.accent, opacity: 0.7, borderRadius: "18px 18px 0 0" }} />
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: c.accent + "18", border: `1px solid ${c.accent}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, marginBottom: 14 }}>{c.icon}</div>
                  <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "1rem", fontWeight: 800, color: textPri, margin: "0 0 8px" }}>{c.title}</h3>
                  <p style={{ fontSize: 13.5, color: textMuted, lineHeight: 1.7, margin: 0 }}>{c.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ── PartnerWhySection (original component) ────────────────────────── */}
          {/* <PartnerWhySection /> */}

          {/* ── CALCULATOR ───────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 80 }}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#10B981", margin: "0 0 6px" }}>Estimate Your Income</p>
            </div>
            <CommissionCalculator isDark={isDark} />
          </motion.div>

          {/* ── PAYOUT STEPS ─────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 80 }}>
            <div style={{ background: payoutBg, border: `1px solid ${payoutBord}`, borderRadius: 22, padding: "40px 36px" }}>
              <div style={{ textAlign: "center", marginBottom: 36 }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#10B981", margin: "0 0 6px" }}>Fully Transparent</p>
                <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 800, color: textPri, margin: "0 0 8px" }}>
                  Payout Frequency & Transparency
                </h2>
                <p style={{ color: textMuted, fontSize: 14, margin: "0 auto", maxWidth: 500 }}>
                  Partner commissions are calculated on confirmed enrollments and released on a monthly cycle. Detailed statements ensure complete transparency.
                </p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
                {PAYOUT_STEPS.map((s, i) => (
                  <motion.div key={s.step} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 14, padding: "20px 18px" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <span style={{ fontSize: 22 }}>{s.icon}</span>
                      <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 11, fontWeight: 800, color: "#10B981", letterSpacing: "0.1em" }}>STEP {s.step}</span>
                    </div>
                    <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: 15, color: textPri, margin: "0 0 6px" }}>{s.title}</h3>
                    <p style={{ fontSize: 13, color: textMuted, margin: 0, lineHeight: 1.65 }}>{s.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 80 }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#EC4899", margin: "0 0 6px" }}>Social Proof</p>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 800, color: textPri, margin: "0 0 8px" }}>
                Trusted by Students & Faculty
              </h2>
              <p style={{ color: textMuted, fontSize: 14, margin: "0 auto", maxWidth: 460 }}>Real feedback from real partners who are growing with GrapplTech.</p>
            </div>
            <div className="ca-marquee-wrap">
              <div className="ca-marquee-track">
                {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
                  <div key={i} style={{ background: tCardBg, border: `1px solid ${tCardBorder}`, borderRadius: 16, padding: "22px 22px 20px", width: 300, flexShrink: 0, position: "relative", overflow: "hidden", boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.3)" : "0 2px 10px rgba(0,0,0,0.06)" }}>
                    <div style={{ position: "absolute", top: 14, right: 16, color: "#EC4899" }}><QuoteIcon /></div>
                    <div style={{ display: "flex", gap: 3, marginBottom: 12 }}>
                      {[...Array(5)].map((_, s) => <StarIcon key={s} />)}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                      <img src={t.image} alt={t.name} style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: `2px solid ${cardBorder}` }} />
                      <div>
                        <p style={{ fontWeight: 700, fontSize: 13.5, color: textPri, margin: 0 }}>{t.name}</p>
                        <p style={{ fontSize: 11.5, color: textMuted, margin: 0 }}>{t.role}</p>
                      </div>
                    </div>
                    <p style={{ fontSize: 13.5, color: textPri, lineHeight: 1.7, margin: 0, fontStyle: "italic" }}>"{t.text}"</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* ── FINAL CTA ─────────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }} style={{ marginTop: 72, textAlign: "center" }}>
            <div style={{
              background: isDark
                ? "linear-gradient(135deg, rgba(2,142,196,0.12) 0%, rgba(99,102,241,0.08) 100%)"
                : "linear-gradient(135deg, rgba(2,142,196,0.07) 0%, rgba(99,102,241,0.04) 100%)",
              border: `1px solid ${cardBorder}`, borderRadius: 22, padding: "52px 36px",
            }}>
              <span style={{ fontSize: 36, display: "block", marginBottom: 16 }}>🤝</span>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.6rem,3.5vw,2.4rem)", fontWeight: 800, color: textPri, margin: "0 0 14px", lineHeight: 1.15 }}>
                Ready to Partner With GrapplTech?
              </h2>
              <p style={{ fontSize: 15, color: textMuted, maxWidth: 500, margin: "0 auto 32px", lineHeight: 1.8 }}>
                Whether you're a student, faculty member, or institution — there's a partner tier built for you. Let's grow together.
              </p>
              <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
                <a href="https://tally.so/r/obeQab" target="_blank" rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#028EC4", color: "#fff", fontWeight: 700, fontSize: 15, padding: "14px 28px", borderRadius: 14, textDecoration: "none", boxShadow: "0 6px 20px rgba(2,142,196,0.35)", transition: "filter 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.1)"}
                  onMouseLeave={e => e.currentTarget.style.filter = "none"}
                >
                  Let's Talk <ArrowIcon />
                </a>
                <a href="mailto:partners@grappl-tech.in"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#FFA900", color: "#000", fontWeight: 700, fontSize: 15, padding: "14px 28px", borderRadius: 14, textDecoration: "none", transition: "filter 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.08)"}
                  onMouseLeave={e => e.currentTarget.style.filter = "none"}
                >
                  partners@grappl-tech.in
                </a>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </>
  );
}